// Lernhilfen im Spielmodus: Hinweis als Pfeil, Urteil über jeden eigenen Zug (sofort zeigen oder nur
// sammeln für „Lerne aus Fehlern“), Eröffnungsname und Züge aus der Bibliothek. Ohne Antwort des
// Servers läuft die Partie einfach ohne die Hilfe weiter.
import type { DrawShape } from '@lichess-org/chessground/draw';
import type { Key } from '@lichess-org/chessground/types';
import { coachApi, type Judgement, type Opening } from './coachapi';
import { loadPref, savePref } from './prefs';

/** eigener Fehler: Stellung davor, gespielter und besserer Zug (UCI) */
export interface Slip {
  before: string;
  played: string;
  playedSan: string;
  judgement: Judgement;
  best: string;
  bestSan: string;
}

type Name = { eco: string | null; name: string };

const SERIOUS: Judgement[] = ['mistake', 'blunder'];
const arrow = (uci: string, brush: string): DrawShape => ({ orig: uci.slice(0, 2) as Key, dest: uci.slice(2, 4) as Key, brush });

export class PlayCoach {
  /** Fehler sofort zeigen und auf „Nochmal“ oder „Weiterspielen“ warten */
  instant: boolean = loadPref('playInstant', false);
  hint: string | null = null;
  pending: Slip | null = null;
  checking = false;
  slips: Slip[] = [];
  hints = 0;
  takebacks = 0;
  /** Eröffnung der Stellung `openingFen`; Name der letzten benannten Stellung der Partie */
  opening: Opening | null = null;
  openingFen = '';
  private named: (Name | null)[] = [];
  private wanted: { fen: string; ply: number; epoch: number } | null = null;
  private asking = false;
  /** Prüfungen nacheinander, nie mehrere gleichzeitig */
  private checks: Promise<unknown> = Promise.resolve();
  private epoch = 0;

  constructor(private readonly redraw: () => void) {}

  setInstant(on: boolean) {
    this.instant = on;
    savePref('playInstant', on);
    this.redraw();
  }

  reset() {
    Object.assign(this, { hint: null, pending: null, checking: false, slips: [], hints: 0, takebacks: 0, opening: null, openingFen: '', named: [] });
    this.epoch++;
  }

  shapes(): DrawShape[] {
    if (this.pending) return [arrow(this.pending.played, 'red'), arrow(this.pending.best, 'green')];
    return this.hint ? [arrow(this.hint, 'green')] : [];
  }

  async askHint(fen: string) {
    const r = await coachApi.hint(fen);
    this.hint = r.best;
    if (r.best) this.hints++;
    this.redraw();
  }

  /** Urteil über den eigenen Zug; Fehler und grobe Fehler kommen (einmal) in die Liste */
  judge(before: string, after: string, played: string, playedSan: string): Promise<Slip | null> {
    const run = this.checks.then(() => this.check(before, after, played, playedSan));
    this.checks = run;
    return run;
  }

  private async check(before: string, after: string, played: string, playedSan: string): Promise<Slip | null> {
    try {
      const r = await coachApi.check(before, after);
      if (!r.judgement || !SERIOUS.includes(r.judgement) || !r.best || !r.bestSan) return null;
      const slip: Slip = { before, played, playedSan, judgement: r.judgement, best: r.best, bestSan: r.bestSan };
      if (!this.slips.some(s => s.before === before && s.played === played)) this.slips.push(slip);
      return slip;
    } catch {
      return null;
    }
  }

  moved() {
    this.hint = null;
  }

  tookBack(plies: number) {
    this.takebacks++;
    this.hint = null;
    this.named.length = Math.min(this.named.length, plies + 1);
    this.opening = null;
  }

  /**
   * Eröffnung nachschlagen; außerhalb von Buch und Bibliothek nicht mehr fragen. Immer nur eine Anfrage
   * offen (nginx begrenzt die Verbindungen je Adresse): kommt eine neue Stellung, solange eine läuft,
   * wird danach nur die neueste gefragt.
   */
  async lookUp(fen: string, ply: number) {
    const o = this.opening;
    if (o && !o.known && o.library.ready && !o.library.games && ply > 0) return;
    this.wanted = { fen, ply, epoch: this.epoch };
    if (this.asking) return;
    this.asking = true;
    while (this.wanted) {
      const w = this.wanted;
      this.wanted = null;
      try {
        const r = await coachApi.opening(w.fen);
        if (w.epoch !== this.epoch) continue; // inzwischen neue Partie
        this.named[w.ply] = r.name ? { eco: r.eco, name: r.name } : null;
        if (this.wanted) continue; // eine neuere Stellung wartet schon
        const last = this.lastName(w.ply);
        this.opening = { ...r, eco: last?.eco ?? null, name: last?.name ?? null };
        this.openingFen = w.fen;
        this.redraw();
      } catch {
        // ohne Eröffnungsangabe
      }
    }
    this.asking = false;
  }

  private lastName(ply: number): Name | null {
    for (let i = Math.min(ply, this.named.length - 1); i >= 0; i--) if (this.named[i]) return this.named[i];
    return null;
  }
}
